import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  VStack,
  HStack,
  Icon,
  Progress,
  useToast
} from '@chakra-ui/react';
import { FaShieldAlt, FaClock } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';

const INACTIVITY_LIMIT_MS = 15 * 60 * 1000; // 15 minutes
const WARNING_BEFORE_MS = 60 * 1000; // Warning shown 60 seconds before expiration

const AdminInactivityGuard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(60);

  const lastActivityRef = useRef(Date.now());
  const timerCheckIntervalRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const isAdminRole = user && (user.role === 'admin' || user.role === 'sub-admin');

  // Reset last activity timestamp
  const handleUserActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (isWarningOpen) {
      setIsWarningOpen(false);
    }
  }, [isWarningOpen]);

  // Extend session when user confirms in modal
  const handleStayLoggedIn = () => {
    lastActivityRef.current = Date.now();
    setIsWarningOpen(false);
    toast({
      title: 'Session Extended',
      description: 'Your admin session remains active.',
      status: 'success',
      duration: 2500,
      isClosable: true
    });
  };

  // Perform logout due to inactivity
  const handleInactivityLogout = useCallback(() => {
    setIsWarningOpen(false);
    logout();
    toast({
      title: 'Session Expired',
      description: 'You were logged out due to 15 minutes of inactivity for security.',
      status: 'warning',
      duration: 5000,
      isClosable: true
    });
    navigate('/login?reason=inactivity');
  }, [logout, navigate, toast]);

  useEffect(() => {
    if (!isAdminRole) {
      return;
    }

    // Attach activity listeners with throttling
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];
    let lastThrottledTime = Date.now();

    const throttledListener = () => {
      const now = Date.now();
      if (now - lastThrottledTime > 1000) {
        lastThrottledTime = now;
        handleUserActivity();
      }
    };

    events.forEach(eventName => {
      window.addEventListener(eventName, throttledListener, { passive: true });
    });

    // Check inactivity every 5 seconds
    timerCheckIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const timeUntilExpire = INACTIVITY_LIMIT_MS - elapsed;

      if (timeUntilExpire <= 0) {
        // Expired completely
        handleInactivityLogout();
      } else if (timeUntilExpire <= WARNING_BEFORE_MS) {
        // Enter warning state
        const remainingSec = Math.ceil(timeUntilExpire / 1000);
        setSecondsRemaining(remainingSec);
        setIsWarningOpen(true);
      } else {
        setIsWarningOpen(false);
      }
    }, 2000);

    return () => {
      events.forEach(eventName => {
        window.removeEventListener(eventName, throttledListener);
      });
      if (timerCheckIntervalRef.current) clearInterval(timerCheckIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [isAdminRole, handleUserActivity, handleInactivityLogout]);

  // Second-by-second countdown when warning is visible
  useEffect(() => {
    if (isWarningOpen) {
      countdownIntervalRef.current = setInterval(() => {
        const elapsed = Date.now() - lastActivityRef.current;
        const timeUntilExpire = INACTIVITY_LIMIT_MS - elapsed;
        const remainingSec = Math.max(0, Math.ceil(timeUntilExpire / 1000));
        setSecondsRemaining(remainingSec);

        if (remainingSec <= 0) {
          handleInactivityLogout();
        }
      }, 1000);
    } else {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    }

    return () => {
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
    };
  }, [isWarningOpen, handleInactivityLogout]);

  if (!isAdminRole || !isWarningOpen) {
    return null;
  }

  return (
    <Modal
      isOpen={isWarningOpen}
      onClose={handleStayLoggedIn}
      isCentered
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <ModalOverlay bg="blackAlpha.800" backdropFilter="blur(8px)" />
      <ModalContent bg="#1a0000" color="white" border="2px solid #ecbd00" borderRadius="2xl" p={2}>
        <ModalHeader borderBottom="1px solid whiteAlpha.200">
          <HStack spacing={3}>
            <Icon as={FaShieldAlt} color="#ecbd00" boxSize={6} />
            <Text fontSize="lg" fontWeight="bold">Admin Session Timeout</Text>
          </HStack>
        </ModalHeader>
        <ModalBody py={6}>
          <VStack spacing={4} align="stretch">
            <Text fontSize="sm" color="whiteAlpha.900">
              For security and devotee data protection, administrative sessions automatically lock after 15 minutes of inactivity.
            </Text>
            
            <HStack justify="center" p={4} bg="whiteAlpha.100" borderRadius="xl" border="1px dashed" borderColor="#ecbd00">
              <Icon as={FaClock} color="#ecbd00" />
              <Text fontSize="xl" fontWeight="bold" color="#ecbd00">
                Auto-locking in {secondsRemaining}s
              </Text>
            </HStack>

            <Progress
              value={(secondsRemaining / 60) * 100}
              colorScheme="yellow"
              size="sm"
              borderRadius="full"
            />
          </VStack>
        </ModalBody>
        <ModalFooter borderTop="1px solid whiteAlpha.200" justify="space-between">
          <Button variant="ghost" color="whiteAlpha.600" size="sm" onClick={handleInactivityLogout}>
            Log Out Now
          </Button>
          <Button
            bgGradient="linear(to-r, #ecbd00, #ff9933)"
            color="#310000"
            fontWeight="bold"
            _hover={{ bgGradient: 'linear(to-r, #ffcc00, #ffaa44)' }}
            onClick={handleStayLoggedIn}
          >
            Stay Signed In
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default AdminInactivityGuard;
